import { DateTime } from 'luxon';

// Plans are priced per billing period, not per month: a yearly plan's
// `price.value` is the whole 59.88 charged up front, while the design shows a
// derived "$4.99 /month". Periods are stored in days, so round to whole months
// (365 → 12, 30 → 1) rather than dividing by an exact 30.4.
export const billingMonths = (plan) => Math.max(1, Math.round((plan?.period?.days || 30) / 30));

export const formatMoney = (amount, currency) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
    return `${currency?.symbol || '$'}${amount.toFixed(2)}`;
};

/** Total charged at each renewal, e.g. "$59.88". */
export const formatPeriodPrice = (price) => formatMoney(price?.value, price?.currency);

/** Per-month equivalent of a period price, e.g. "$4.99" for $59.88/year. */
export const formatMonthlyPrice = (price, plan) => {
    if (typeof price?.value !== 'number') return '';
    return formatMoney(price.value / billingMonths(plan), price.currency);
};

/**
 * Whole-percent saving of the current price against the pre-discount
 * `basePrice`. Returns null when there is no offer, or when the numbers don't
 * describe a real discount — so a bad price row shows no badge rather than
 * "0% off" or a negative one.
 */
/**
 * What the plan costs once any introductory offer is spent — the price it
 * settles at, which is what the card leads with.
 *
 * `basePrice` is only present when the backend has applied an offer to this
 * customer, so it is the list price when there is one and `price` is the list
 * price when there isn't.
 */
export const listPrice = (plan) => plan?.basePrice || plan?.price;

/**
 * The discounted price of a plan's first billing period, or null when this
 * customer has no offer on it (HORO-10557).
 *
 * Offers of this kind are single-use and renewals ignore them, so an offer on a
 * plan always discounts exactly one period: the first. That holds for the
 * $4.99 first month and equally for the after-decline discount, which is the
 * same mechanism — the API returns both as a lowered `price` against a
 * `basePrice`, with nothing to tell them apart. Which period it is comes from
 * the plan, so the yearly plan would read "your first year".
 */
export const introPrice = (plan) => {
    const base = plan?.basePrice?.value;
    const current = plan?.price?.value;
    if (typeof base !== 'number' || typeof current !== 'number') return null;

    return current < base ? plan.price : null;
};

export const discountPercent = (plan) => {
    const base = plan?.basePrice?.value;
    const current = plan?.price?.value;
    if (typeof base !== 'number' || typeof current !== 'number') return null;
    if (base <= 0 || current >= base) return null;
    const percent = Math.round((1 - current / base) * 100);
    return percent > 0 ? percent : null;
};

/**
 * What a plan saves against the alternatives, and the per-month figure to
 * strike through beside it (Figma `5788:2647`).
 *
 * What the same month costs on the dearest alternative. The design's yearly
 * tile strikes `$9.99` — the monthly plan's price, not a base price the yearly
 * plan has ever had — to say the same month bought annually costs half as much.
 * `discountPercent` alone can't drive the tile: the annual plan carries no
 * `basePrice`, and the seed migration says it never will.
 *
 * Compared on list prices throughout (HORO-10557). A plan's own introductory
 * offer is deliberately not a saving here: it buys one period, not the
 * relationship between the plans, and the card states it as copy instead. Were
 * it counted, the $4.99 first month would both put a rival badge on the monthly
 * tile and drag the reference down to $4.99, wiping out the yearly tile's claim
 * — the two would read identically at $4.99 with the wrong one badged.
 *
 * Returns null when there is nothing to claim — a single plan on sale, the
 * dearest plan itself, or prices that don't describe a real saving.
 */
export const planSaving = (plan, plans) => {
    const monthly = monthlyValue(plan);
    if (monthly === null) return null;

    const reference = (plans || []).reduce((dearest, other) => {
        const value = monthlyValue(other);
        return value !== null && value > dearest ? value : dearest;
    }, 0);

    if (!(reference > monthly)) return null;

    const percent = Math.round((1 - monthly / reference) * 100);
    if (percent <= 0) return null;

    return { percent, reference, currency: plan?.price?.currency };
};

/**
 * Plans in the order they are offered: shortest billing period first, so the
 * monthly card sits left of the yearly one (Figma `5788:2647`).
 *
 * Sorted rather than taken as the API returns them, which is insertion order in
 * the catalogue and put the yearly plan first. Price breaks the tie so two plans
 * on the same period still land in a stable order.
 */
/**
 * The plan an upsell should lead with: the one carrying a saving, otherwise the
 * first on offer. Shared so the paywall's default tile and the "Go Premium
 * instead" link out of the pack checkout cannot drift apart — a customer who
 * follows that link expects the plan they were just being shown.
 */
export const defaultAdvertisedPlan = (plans) => {
    const ordered = sortPlansForDisplay(plans);
    return ordered.find((plan) => planSaving(plan, ordered) !== null) || ordered[0] || null;
};

export const sortPlansForDisplay = (plans) =>
    [...(plans || [])].sort((a, b) => billingMonths(a) - billingMonths(b) || (a?.price?.value ?? Infinity) - (b?.price?.value ?? Infinity));

// Per-month price as a plain number, or null when the plan has no usable price.
const monthlyValue = (plan) => {
    const value = listPrice(plan)?.value;
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    return value / billingMonths(plan);
};

/**
 * The line-item breakdown shown on desktop (Figma 5179-16560): what the period
 * costs at full price, what the discount takes off, and what's actually charged
 * today.
 *
 * Returns null when there's no price to break down. The discount fields are null
 * for a plan without an offer, so the caller renders the row or not on that.
 *
 * The subtraction is computed rather than taken from the design's rounded
 * figures — with $119.98 and $59.88 the real difference is $60.10, and a
 * breakdown whose arithmetic doesn't add up is worse than no breakdown.
 */
export const priceBreakdown = (plan) => {
    const total = plan?.price?.value;
    if (typeof total !== 'number') return null;

    const base = plan?.basePrice?.value;
    const percent = discountPercent(plan);
    const hasDiscount = typeof base === 'number' && percent !== null;

    return {
        months: billingMonths(plan),
        currency: plan?.price?.currency,
        periodTotal: hasDiscount ? base : total,
        discount: hasDiscount ? Number((base - total).toFixed(2)) : null,
        percent: hasDiscount ? percent : null,
        total
    };
};

/**
 * i18n key describing how often the plan bills — `monthly`, `yearly`, or the
 * `everyMonths` fallback for anything else (e.g. a 3-month plan).
 */
export const billingFrequencyKey = (plan) => {
    const months = billingMonths(plan);
    if (months === 1) return 'monthly';
    if (months === 12) return 'yearly';
    return 'everyMonths';
};

/**
 * When the first renewal falls, as a localised date string ("6 August 2027").
 * `from` is injectable so tests don't depend on the current date.
 */
export const renewalDate = (plan, from = DateTime.now()) =>
    from.plus({ days: plan?.period?.days || 30 }).toLocaleString(DateTime.DATE_FULL);

/**
 * Cheapest plan by monthly-equivalent price — what "from $4.99/mo" refers to.
 *
 * Compared on the derived per-month figure rather than `price.value`, because
 * the yearly plan charges the larger amount up front while being the cheaper of
 * the two per month. Sorting on the raw value would advertise the dearer plan as
 * the bargain.
 *
 * A quote, never a charge: HORO-10554 removed the selector that let a purchase
 * path resolve a plan on its own, and this is not a way back to one.
 *
 * `monthsOf` stays injectable for the tests that already pass it explicitly.
 */
export const cheapestMonthlyPlan = (plans, monthsOf = billingMonths) => {
    const priced = (plans || []).filter((plan) => typeof plan?.price?.value === 'number');
    if (!priced.length) return null;

    return priced.reduce((cheapest, plan) =>
        plan.price.value / monthsOf(plan) < cheapest.price.value / monthsOf(cheapest) ? plan : cheapest
    );
};
